/**
 * TemplateBottomBar Component
 *
 * Bottom action bar for template editing with save and delete actions.
 * Only visible when editing a user template with edit permissions.
 * Includes an autosave status indicator above the action buttons.
 */

import React from 'react';
import { type AutosaveState, AutosaveStatus } from '@components/AutosaveStatus';
import {
  Delete as DeleteIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
} from '@mui/material';

import { LAYOUT_DIMENSIONS } from '../constants/layout';

interface TemplateBottomBarProps {
  /** Whether save is in progress */
  isSaving: boolean;
  /** Whether user can delete this template */
  canDelete: boolean;
  /** Current autosave status */
  autosaveStatus: AutosaveState;
  /** Whether there are unsaved changes */
  isDirty: boolean;
  /** Last saved timestamp */
  lastSavedAt: Date | null;
  /** Callback to save template */
  onSave: () => void;
  /** Callback to delete template */
  onDelete: () => void;
}

export const TemplateBottomBar: React.FC<TemplateBottomBarProps> = ({
  isSaving,
  canDelete,
  autosaveStatus,
  isDirty,
  lastSavedAt,
  onSave,
  onDelete,
}) => {
  return (
    <Box
      component="footer"
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 1,
        py: 1.5,
        px: { xs: 2, sm: 3, md: 4 },
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        minHeight: LAYOUT_DIMENSIONS.BOTTOM_BAR_HEIGHT,
      }}
    >
      {/* Autosave Status Indicator - positioned above the buttons */}
      <AutosaveStatus
        status={autosaveStatus}
        isDirty={isDirty}
        lastSavedAt={lastSavedAt}
        verbose
      />

      {/* Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {/* Delete Button */}
        {canDelete && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={onDelete}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Delete
          </Button>
        )}

        {/* Save Button */}
        <Button
          variant="contained"
          startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={onSave}
          disabled={isSaving}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            minWidth: 120,
          }}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </Box>
    </Box>
  );
};
